import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Key, UploadSimple } from "@phosphor-icons/react";
import { SegTabs } from "@/components/SegTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppHeader, photoSrc } from "@/components/AppHeader";
import { EmailingTab } from "@/components/EmailingTab";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const TABS = [
  ["general", "General"],
  ["emailing", "Emailing"],
];

export default function Account() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "emailing" ? "emailing" : "general";
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [icon, setIcon] = useState(user?.icon || null);
  const [uploading, setUploading] = useState(false);
  const iconInput = useRef(null);

  useEffect(() => {
    setIcon(user?.icon || null);
  }, [user]);

  const uploadIcon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/me/icon", fd);
      setIcon(data.icon);
      toast.success("Icon updated — refresh to see it in the menu");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Icon upload failed",
      );
    }
    setUploading(false);
  };

  const removeIcon = async () => {
    await api.delete("/auth/me/icon");
    setIcon(null);
    toast.success("Icon removed");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const field = (label, key, props = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`account-${key.replace(/_/g, "-")}-input`}
        type="password"
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
        {...props}
      />
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Profile" />
      <main className="relative z-10 mx-auto max-w-3xl px-6 pb-20 pt-8">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Profile
          </h1>
          <SegTabs
            testId="profile-tabs"
            size="sm"
            tabs={TABS.map(([key, label]) => ({ key, label }))}
            value={tab}
            onChange={(t) => setParams({ tab: t.key }, { replace: true })}
          />
        </div>
        <p className="mt-2 text-sm text-zinc-500" data-testid="account-email">
          Signed in as {user?.email} · {user?.role}
        </p>

        {tab === "emailing" && (
          <div className="mt-8">
            <EmailingTab />
          </div>
        )}

        {tab === "general" && (
          <>
            <h2 className="mt-8 text-lg font-bold">My Password</h2>

            <form
              onSubmit={submit}
              className="mt-4 space-y-5"
              data-testid="password-form"
            >
              {field("Current Password", "current_password")}
              {field("New Password", "new_password", { minLength: 6 })}
              {field("Confirm New Password", "confirm", { minLength: 6 })}

              {error && (
                <p
                  data-testid="account-error"
                  className="text-sm font-medium text-red-400"
                >
                  {error}
                </p>
              )}

              <Button
                data-testid="change-password-button"
                type="submit"
                disabled={busy}
                className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Key size={18} weight="bold" className="mr-2" />
                {busy ? "Updating…" : "Change Password"}
              </Button>
            </form>

            <div
              className="mt-10 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="user-icon-panel"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                User Icon
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                Your personal photo, shown next to your name in the side menu
                and top bar. Square images work best.
              </p>
              <div className="mt-4 flex items-center gap-4">
                {icon ? (
                  <img
                    src={photoSrc(icon)}
                    alt="Your icon"
                    data-testid="user-icon-preview"
                    className="h-16 w-16 rounded-full border border-zinc-800 object-cover"
                  />
                ) : (
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-500/15 text-lg font-bold uppercase text-indigo-300">
                    {(user?.name || user?.email || "?").slice(0, 2)}
                  </span>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    data-testid="upload-user-icon-button"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => iconInput.current?.click()}
                    className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <UploadSimple size={15} weight="bold" className="mr-2" />
                    {uploading
                      ? "Uploading…"
                      : icon
                        ? "Replace Icon"
                        : "Upload Icon"}
                  </Button>
                  {icon && (
                    <Button
                      type="button"
                      data-testid="remove-user-icon-button"
                      variant="outline"
                      onClick={removeIcon}
                      className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest text-red-400 hover:bg-zinc-800"
                    >
                      Remove
                    </Button>
                  )}
                  <input
                    ref={iconInput}
                    data-testid="user-icon-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadIcon}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
